import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { musicProductDigitalTabs } from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiMusicNotes, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostMusicProductDigital } from 'services/music-product-digital';
import { usePostMusicProductDigitalImage } from 'services/music-product-digital/image';
import { usePostMusicProductDigitalImageThumbnail } from 'services/music-product-digital/imageThumbnail';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const CreateMusicProductDigital = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const createMusicProdutc = usePostMusicProductDigital();
  const addImage = usePostMusicProductDigitalImage();
  const addImageThumbnail = usePostMusicProductDigitalImageThumbnail();

  const mutateOptions = useMutateOptions<IMusicProductDigitalForm>();

  const goToIndividualMusicProductDigital = (id: string) =>
    navigate(`/music-product-digital/${id}/${getLastTabPathname(pathname, 3)}`);

  const onCreateMusicProductDigitalWithFile = async (
    id: string,
    file?: File,
    thumbnailFile?: File
  ) => {
    file &&
      (await addImage.mutateAsync(
        { file, id },
        {
          onError: error => {
            onErrorShowToast(error, 'toast-message.add-album-cover');
          },
        }
      ));
    thumbnailFile &&
      (await addImageThumbnail.mutateAsync(
        { file: thumbnailFile, id },
        {
          onError: error => {
            onErrorShowToast(error, 'toast-message.add-album-cover');
          },
        }
      ));

    goToIndividualMusicProductDigital(id);
  };

  const onCreateMusicProductDigital = async (
    values: IMusicProductDigitalForm,
    helpers: FormikHelpers<IMusicProductDigitalForm>
  ) => {
    const musicProductDigitalMutation = {
      ...middleware.musicProductDigital.formToQuery(values),
      waitingForFile: !!values.file,
    };

    await createMusicProdutc.mutateAsync(musicProductDigitalMutation, {
      ...mutateOptions(helpers),
      onSuccess: async data =>
        values.file || values.thumbnailFile
          ? onCreateMusicProductDigitalWithFile(
              data.id,
              values.file,
              values.thumbnailFile
            )
          : goToIndividualMusicProductDigital(data.id),
    });
  };

  return (
    <Form.MusicProductDigital
      i18nextPrefix='music-product-digital'
      onSubmit={onCreateMusicProductDigital}
      disableTabs={[
        musicProductDigitalTabs.delivery,
        musicProductDigitalTabs.statistics,
        musicProductDigitalTabs.smartlink,
        musicProductDigitalTabs.assetIds,
      ]}
      customBackItem={
        <Button onlyIcon onClick={() => navigate('/music-product-digital')}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.create')} />
          <Button.Icon icon={<PiMusicNotes />} />
        </Button>
      }
    />
  );
};

export default CreateMusicProductDigital;
